import {
  Answer,
  Attempt,
  AttemptSummary,
  ExamSummary,
  User,
  LearningStatistics,
  RandomTag,
  BankPage,
} from "./learning_model";

export class HttpError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
  }
}
export class LearningHttpRepository {
  private readonly server =
    process.env.REACT_APP_API_URL || "http://localhost:4001/api";
  private csrf = "";
  private csrfRequest?: Promise<void>;
  private async ensureCsrf() {
    if (this.csrf) return;
    if (!this.csrfRequest)
      this.csrfRequest = this.request<{ csrfToken: string }>(
        "GET",
        "/auth/csrf",
      )
        .then((result) => {
          this.csrf = result.csrfToken;
        })
        .finally(() => {
          this.csrfRequest = undefined;
        });
    await this.csrfRequest;
  }
  private async request<T>(
    method: string,
    path: string,
    data?: unknown,
  ): Promise<T> {
    if (method !== "GET") await this.ensureCsrf();
    try {
      const response = await fetch(this.server + path, {
        method,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(method !== "GET" ? { "X-CSRF-Token": this.csrf } : {}),
        },
        body: data === undefined ? undefined : JSON.stringify(data),
      });
      const result = await response.json();
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) this.csrf = "";
        throw new HttpError(
          result.error || "Не удалось выполнить запрос",
          response.status,
        );
      }
      return result as T;
    } catch (error) {
      if (error instanceof HttpError) throw error;
      throw new HttpError(
        "Не удалось связаться с сервером. Проверьте соединение и повторите запрос.",
        0,
      );
    }
  }
  me = () => this.request<{ user: User }>("GET", "/auth/me");
  login = async (username: string, password: string) => {
    const result = await this.request<{ user: User; csrfToken: string }>(
      "POST",
      "/auth/login",
      { username, password },
    );
    this.csrf = result.csrfToken;
    return result.user;
  };
  logout = async () => {
    await this.request("POST", "/auth/logout", {});
    this.csrf = "";
  };
  statistics = () =>
    this.request<LearningStatistics>("GET", "/learning/statistics");
  randomTags = () => this.request<RandomTag[]>("GET", "/learning/random-tags");
  startRandom = (count: number, tags: string[], totalScore: number) =>
    this.request<Attempt>("POST", "/learning/random-tests", {
      count,
      tags,
      totalScore,
    });
  exams = (page: number, kind: "test" | "variant" = "test") =>
    this.request<ExamSummary[]>(
      "GET",
      `/learning/exams?page=${page}&kind=${kind}`,
    );
  bankTopics = () => this.request<RandomTag[]>("GET", "/learning/bank/topics");
  bank = (page: number, tag: string, q: string) =>
    this.request<BankPage>(
      "GET",
      `/learning/bank/tasks?${new URLSearchParams({ page: String(page), ...(tag ? { tag } : {}), ...(q ? { q } : {}) })}`,
    );
  startBankTask = (id: string) =>
    this.request<Attempt>(
      "POST",
      `/learning/bank/tasks/${encodeURIComponent(id)}/attempts`,
      {},
    );
  construct = (
    title: string,
    selections: { tag: string; count: number }[],
    totalScore: number,
  ) =>
    this.request<Attempt>("POST", "/learning/constructed-tests", {
      title,
      selections,
      totalScore,
    });
  attempts = (page: number) =>
    this.request<AttemptSummary[]>("GET", `/learning/attempts?page=${page}`);
  start = (id: string) =>
    this.request<Attempt>(
      "POST",
      `/learning/exams/${encodeURIComponent(id)}/attempts`,
      {},
    );
  attempt = (id: string) =>
    this.request<Attempt>(
      "GET",
      `/learning/attempts/${encodeURIComponent(id)}`,
    );
  save = (id: string, revision: number, answers: Answer[]) =>
    this.request<Attempt>(
      "PUT",
      `/learning/attempts/${encodeURIComponent(id)}/answers`,
      { revision, answers },
    );
  finish = (id: string, revision: number) =>
    this.request<Attempt>(
      "POST",
      `/learning/attempts/${encodeURIComponent(id)}/finish`,
      { revision },
    );
}
